import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { GameState, Round } from '@flugwettrennen/shared';
import { api } from '../api';
import { ChallengeBadge } from '../components/ChallengeBadge';
import { RouteMap } from '../components/RouteMap';
import { dateTime, hhmm, humanDuration } from '../lib/format';
import { goalLabel } from '../lib/goals';

export function ResultPage() {
  const { id = '' } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [round, setRound] = useState<Round | null>(null);
  const [gstate, setGstate] = useState<GameState | null>(null);

  useEffect(() => {
    api.rounds.get(id).then((r) => {
      setRound(r.round);
      setGstate(r.state);
    });
  }, [id]);

  async function replay() {
    if (!round) return;
    const res = await api.rounds.create({
      name: `${round.name} ↻`,
      goalTypes: round.goalTypes,
      startAirportIata: round.startAirportIata,
      destAirportIata: round.destAirportIata,
      startDateTime: new Date().toISOString(),
      mode: round.mode,
      options: round.options as unknown as Record<string, unknown>,
    });
    navigate(`/round/${res.round.id}`);
  }

  if (!round || !gstate) return <div className="container page"><div className="spinner" /></div>;

  const dur = humanDuration(round.totalDurationMinutes);
  const airports = new Set(gstate.legs.flatMap((l) => [l.from.iata, l.to.iata])).size;

  return (
    <div className="container page stack">
      <div className="center stack" style={{ alignItems: 'center', gap: 4 }}>
        <div className="eyebrow">{goalLabel(t, round.goalTypes)}</div>
        <h1>{t('result.title')}</h1>
        <p className="muted">{round.name}</p>
        <ChallengeBadge code={round.challengeCode} />
      </div>

      <div className="stat-row">
        <div className="card center">
          <div className="eyebrow">{t('result.totalTime')}</div>
          <div className="num">{t('common.duration', { h: dur.h, m: dur.m })}</div>
        </div>
        <div className="card center">
          <div className="eyebrow">{t('result.totalFlights')}</div>
          <div className="num">{gstate.legs.length}</div>
        </div>
        <div className="card center">
          <div className="eyebrow">{t('result.airports')}</div>
          <div className="num">{airports}</div>
        </div>
      </div>

      <RouteMap legs={gstate.legs} />

      <div className="card">
        <div className="eyebrow" style={{ marginBottom: 8 }}>{t('game.legs')}</div>
        <div className="table-wrap">
          <table className="mono" style={{ fontSize: '0.85rem' }}>
            <tbody>
              {gstate.legs.map((l) => (
                <tr key={l.order} style={{ borderBottom: '1px solid var(--line)' }}>
                  <td style={{ padding: '6px 8px', color: 'var(--muted)' }}>{l.order}</td>
                  <td style={{ padding: '6px 8px' }}>{l.from.iata} → {l.to.iata}</td>
                  <td style={{ padding: '6px 8px' }}>{l.flightNumber}</td>
                  <td style={{ padding: '6px 8px', color: 'var(--muted)' }}>{l.airlineName}</td>
                  <td style={{ padding: '6px 8px' }}>{hhmm(l.actualDeparture)} → {hhmm(l.actualArrival)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="row" style={{ justifyContent: 'center' }}>
        <Link className="btn" to="/">{t('result.backToDirectory')}</Link>
        <Link className="btn" to={`/round/${round.id}/settings`} data-testid="round-settings-link">
          {t('settings.link')}
        </Link>
        <button className="btn btn-go" onClick={() => void replay()}>{t('result.replay')}</button>
      </div>
      <p className="center muted mono" style={{ fontSize: '0.8rem' }}>{dateTime(round.startDateTime)} · {round.startAirportIata} → {round.destAirportIata}</p>
    </div>
  );
}
